/**
 * "AI ดูแลแชท (ทดสอบ)" — api/app/ai/aimode_routes.py · docs/ai-mode.md.
 * The row/detail fields (`ai_mode`, `ai_handoff`) live on ConversationSummary (../types.ts).
 */
import type { ConversationSummary, StaffRef } from '../types'

/** GET /conversations/:id/ai-mode */
export interface AiModeView {
  conversation_id: string
  /** The owner's master switch (ตั้งค่า › AI › AI ดูแลแชท). */
  enabled: boolean
  /** May "ให้ AI ดูแล" be pressed now — else the Thai reason for the disabled button. */
  can_turn_on: { ok: boolean; reason: string | null }
  /** The open session, else the latest one; null = never. */
  session: AiSession | null
  /** Newest first (S2's ai_sales_turns); [] when none or the table does not exist yet. */
  recent_turns: AiTurn[]
}

export interface AiSession {
  id: string
  open: boolean
  by: StaffRef
  started_at: string
  ended_at: string | null
  /** staff_took_over | turned_off | handoff | order_done | closed | error */
  end_reason: string | null
  handoff: { code: string; note: string | null; at: string | null } | null
  order_id: string | null
  turns: number
  errors: number
  /** What the AI understood — owned by the sales brain. Common keys: stage, package, items, customer, address, quote, slip. */
  state: Record<string, unknown>
}

export interface AiTurn {
  id: string
  at: string | null
  trigger_message_id: string | null
  /** Tool calls of the turn as the brain logged them: usually {name, ok?, ms?, …}. */
  tools: unknown[]
  output: Record<string, unknown>
  debug: Record<string, unknown> | null
  error: string | null
  ms: number | null
  model: string | null
  cost_usd: number | null
}

/** Screen events on the socket (collab channel, no DB rows). */
export type AiModeEvent =
  | {
      type: 'ai.handoff'
      conversation_id: string
      session_id: string
      /** Who gets the alert: the chat's owner, else who started the AI. */
      staff_id: string | null
      code: string
      note: string
    }
  | { type: 'ai.turn'; conversation_id: string; session_id: string; result: string }
  /**
   * "AI กำลังคิด / กำลังพิมพ์" (api/app/ai/aimode_runner.py busy): thinking → typing (once per bubble, `left` bubbles
   * still to go, `seconds` until this one is sent) → idle. `trigger_id` = the customer message being answered (orders
   * the events of the two API instances) · `ttl` seconds: drop the state if "idle" never comes.
   */
  | {
      type: 'ai.busy'
      conversation_id: string
      session_id: string
      trigger_id: string
      state: 'thinking' | 'typing' | 'idle'
      at: number
      ttl: number
      seconds?: number
      left?: number
    }

export function isAiModeEvent(v: unknown): v is AiModeEvent {
  if (typeof v !== 'object' || v === null) return false
  const t = (v as { type?: unknown }).type
  return (
    (t === 'ai.handoff' || t === 'ai.turn' || t === 'ai.busy') &&
    typeof (v as { conversation_id?: unknown }).conversation_id === 'string'
  )
}

export interface AiModeApi {
  view(convId: string): Promise<AiModeView>
  /** Any signed-in staff. on: 409/422 with a Thai `detail` when not allowed. */
  set(convId: string, on: boolean): Promise<ConversationSummary>
  /** "รับต่อ" on the hand-off banner: the warning goes away and the chat is assigned to me. */
  acceptHandoff(convId: string): Promise<ConversationSummary>
}

/** Thai name of a hand-off code (the note says the details). */
export const HANDOFF_LABEL: Record<string, string> = {
  health_allergy: 'สุขภาพหรือแพ้อาหาร',
  complaint: 'ลูกค้าไม่พอใจ',
  discount: 'ขอส่วนลด',
  wants_human: 'ขอคุยกับพนักงาน',
  payment_info_missing: 'ยังไม่มีข้อมูลการชำระเงิน',
  menu_info: 'ถามรายละเอียดเมนูที่ยังไม่มีในเมนูรอบนี้',
  stuck: 'คุยนานเกินกำหนด',
  error: 'AI ขัดข้อง',
}
